import { useEffect, useState } from "react";
import toast from "../../../../utils/toast";
import {
  getAmenitiesList,
  toggleAmenitiesStatus,
  createAmenities,
  getAmenitiesById,
  updateAmenitiesById,
  deleteAmenitiesById,
} from "../../../../services/admin/adminServices";

export default function AmenitiesMaster() {
  const [isAmenitiesList, setAmenitiesList] = useState({});
  const [listLoading, setListLoading] = useState(true);
  const [isModalOpenAddAmenity, setIsModalOpenAddAmenity] = useState(false);
  const [addAmenityForm, setAddAmenityForm] = useState({
    amenityName: "",
    icon: "",
    description: "",
    showMode: "both",
    sortOrder: 0,
    isActive: true,
  });
  const [addAmenityErrors, setAddAmenityErrors] = useState({});
  const [addAmenityLoading, setAddAmenityLoading] = useState(false);
  const [isModalOpenEditAmenity, setIsModalOpenEditAmenity] = useState(false);
  const [isModalOpenDeleteAmenity, setIsModalOpenDeleteAmenity] =
    useState(false);
  const [selectedAmenityId, setSelectedAmenityId] = useState(null);
  const [editAmenityForm, setEditAmenityForm] = useState({});
  const [editAmenityErrors, setEditAmenityErrors] = useState({});
  const [editAmenityLoading, setEditAmenityLoading] = useState(false);

  useEffect(() => {
    fetchAmenitiesList();
  }, []);

  const fetchAmenitiesList = async () => {
    setListLoading(true);
    try {
      const res = await getAmenitiesList();
      if (res.statusCode === 1) setAmenitiesList(res.data);
    } catch (err) {
      console.log("Failed to fetch amenities");
    } finally {
      setListLoading(false);
    }
  };

  const updateAmenityRow = (amenityId, field, value) => {
    setAmenitiesList((prev) => ({
      ...prev,
      amenities: prev.amenities.map((a) =>
        a.amenityId === amenityId ? { ...a, [field]: value } : a,
      ),
    }));
  };

  const openAddAmenityModal = () => {
    setAddAmenityForm({
      amenityName: "",
      icon: "",
      description: "",
      showMode: "both",
      sortOrder: 0,
      isActive: true,
    });
    setAddAmenityErrors({});
    setIsModalOpenAddAmenity(true);
  };

  const closeAddAmenityModal = () => {
    setIsModalOpenAddAmenity(false);
    setAddAmenityForm({
      amenityName: "",
      icon: "",
      description: "",
      showMode: "both",
      sortOrder: 0,
      isActive: true,
    });
    setAddAmenityErrors({});
  };

  const handleAddAmenityChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAddAmenityForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setAddAmenityErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleCreateAmenity = async () => {
    const e = {};
    if (!addAmenityForm.amenityName)
      e.amenityName = "Amenity name is required.";
    if (!addAmenityForm.icon) e.icon = "Icon is required.";
    if (Object.keys(e).length) {
      setAddAmenityErrors(e);
      return;
    }

    setAddAmenityLoading(true);
    try {
      const res = await createAmenities(addAmenityForm);
      if (res.statusCode === 1) {
        toast.success(res.message);
        closeAddAmenityModal();
        fetchAmenitiesList();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setAddAmenityLoading(false);
    }
  };

  const handleToggleAmenity = async (amenity) => {
    const newValue = !amenity.isActive;
    updateAmenityRow(amenity.amenityId, "isActive", newValue);

    try {
      const res = await toggleAmenitiesStatus(amenity.amenityId);
      if (res.statusCode === 1) {
        toast.success(res.message);
      } else {
        toast.error(res.message);
        updateAmenityRow(amenity.amenityId, "isActive", !newValue);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
      updateAmenityRow(amenity.amenityId, "isActive", !newValue);
    }
  };

  const openEditAmenityModal = async (id) => {
    try {
      const res = await getAmenitiesById(id);
      if (res.statusCode === 1) {
        const d = res.data;
        setEditAmenityForm({
          amenityName: d.amenityName || "",
          icon: d.icon || "",
          description: d.description || "",
          showMode: d.showMode || "both",
          sortOrder: d.sortOrder || 0,
          isActive: d.isActive,
        });
        setSelectedAmenityId(id);
        setEditAmenityErrors({});
        setIsModalOpenEditAmenity(true);
      }
    } catch (err) {
      toast.error("Failed to load amenity details.");
    }
  };

  const closeEditAmenityModal = () => {
    setIsModalOpenEditAmenity(false);
    setEditAmenityForm({});
    setEditAmenityErrors({});
    setSelectedAmenityId(null);
  };

  const handleEditAmenityChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditAmenityForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setEditAmenityErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleUpdateAmenity = async () => {
    const e = {};
    if (!editAmenityForm.amenityName)
      e.amenityName = "Amenity name is required.";
    if (!editAmenityForm.icon) e.icon = "Icon is required.";
    if (Object.keys(e).length) {
      setEditAmenityErrors(e);
      return;
    }

    setEditAmenityLoading(true);
    try {
      const res = await updateAmenitiesById(selectedAmenityId, editAmenityForm);
      if (res.statusCode === 1) {
        toast.success(res.message);
        closeEditAmenityModal();
        fetchAmenitiesList();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setEditAmenityLoading(false);
    }
  };

  const confirmDeleteAmenity = async () => {
    try {
      const res = await deleteAmenitiesById(selectedAmenityId);
      if (res.statusCode === 1) {
        toast.success(res.message);
        setIsModalOpenDeleteAmenity(false);
        setSelectedAmenityId(null);
        fetchAmenitiesList();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    }
  };

  return (
    <>
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title" id="page-title">
              Amenities Master
            </div>
            <div className="page-sub" id="page-sub">
              System › Configure amenities shown during property setup
            </div>
          </div>
          <div className="topbar-right" id="topbar-right">
            <button className="btn btn-primary" onClick={openAddAmenityModal}>
              + Add Amenity
            </button>
          </div>
        </div>
        <div className="content" id="content-area">
          <div className="info-box mb16">
            <strong>Amenities Master</strong> — define all amenities available
            on your platform. Enabled amenities appear as selectable options
            when property owners add a society or property. You can control what
            tenants see: just the name, the description, or both.
          </div>
          <div className="stat-grid mb16">
            <div className="stat-card">
              <div className="stat-icon">✨</div>
              <div className="stat-label">Total Amenities</div>
              <div className="stat-value">
                {isAmenitiesList?.totalAmenities}
              </div>
              <div className="stat-delta delta-neu">Defined</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">✅</div>
              <div className="stat-label">Enabled</div>
              <div className="stat-value">{isAmenitiesList?.enabled}</div>
              <div className="stat-delta delta-up">Selectable by owners</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🔕</div>
              <div className="stat-label">Disabled</div>
              <div className="stat-value">{isAmenitiesList?.disabled}</div>
              <div className="stat-delta delta-dn">Hidden from owners</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">👁</div>
              <div className="stat-label">Show Both</div>
              <div className="stat-value">{isAmenitiesList?.showBoth}</div>
              <div className="stat-delta delta-neu">Name + description</div>
            </div>
          </div>
          <div className="card">
            <div style={{ overflowX: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Icon</th>
                    <th>Amenity Name</th>
                    <th>Description</th>
                    <th>Enabled</th>
                    <th>Tenant Display</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {listLoading ? (
                    Array.from({ length: 8 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 7 }).map((_, j) => (
                          <td key={j}>
                            <div
                              style={{
                                height: 14,
                                borderRadius: 6,
                                background:
                                  "linear-gradient(90deg, var(--bg-secondary) 25%, var(--border-light) 50%, var(--bg-secondary) 75%)",
                                backgroundSize: "200% 100%",
                                animation: "shimmer 1.4s infinite",
                                width: j === 2 ? "80%" : "60%",
                              }}
                            />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : isAmenitiesList?.amenities?.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        style={{
                          textAlign: "center",
                          color: "var(--text-tertiary)",
                          padding: 24,
                        }}
                      >
                        No amenities found.
                      </td>
                    </tr>
                  ) : (
                    isAmenitiesList?.amenities?.map((amenity, index) => (
                      <tr key={amenity.amenityId}>
                        <td className="sn-col">{index + 1}</td>
                        <td style={{ fontSize: 22, textAlign: "center" }}>
                          {amenity.icon}
                        </td>
                        <td>
                          <div style={{ fontWeight: 600 }}>
                            {amenity.amenityName}
                          </div>
                        </td>
                        <td
                          style={{
                            fontSize: 11,
                            color: "var(--text-secondary)",
                            maxWidth: 280,
                          }}
                        >
                          {amenity.description}
                        </td>
                        <td>
                          <label
                            className="toggle"
                            style={{ display: "inline-block" }}
                          >
                            <input
                              type="checkbox"
                              checked={amenity.isActive}
                              onChange={() => handleToggleAmenity(amenity)}
                            />
                            <span className="toggle-slider" />
                          </label>
                        </td>
                        <td>
                          <select
                            value={amenity.showMode}
                            onChange={(e) =>
                              updateAmenityRow(
                                amenity.amenityId,
                                "showMode",
                                e.target.value,
                              )
                            }
                            style={{ fontSize: 12, padding: "4px 6px" }}
                          >
                            <option value="name">Name Only</option>
                            <option value="description">
                              Description Only
                            </option>
                            <option value="both">Name + Description</option>
                          </select>
                        </td>
                        <td>
                          <div className="td-actions">
                            <span
                              className="action-icon"
                              title="Edit"
                              onClick={() =>
                                openEditAmenityModal(amenity.amenityId)
                              }
                            >
                              <span className="tip">Edit</span>✎
                            </span>
                            <span
                              className="action-icon red"
                              title="Delete"
                              onClick={() => {
                                setSelectedAmenityId(amenity.amenityId);
                                setIsModalOpenDeleteAmenity(true);
                              }}
                            >
                              <span className="tip">Delete</span>🗑
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        {isModalOpenAddAmenity && (
          <div className="modal-overlay open">
            <div className="modal" style={{ width: 560 }}>
              <div className="modal-header">
                <div className="modal-title">Add Amenity</div>
                <button className="modal-close" onClick={closeAddAmenityModal}>
                  ✕
                </button>
              </div>

              <div className="modal-body">
                <div className="form-grid mb12">
                  <div className="form-group">
                    <label>Amenity Name *</label>
                    <input
                      type="text"
                      name="amenityName"
                      value={addAmenityForm.amenityName}
                      onChange={handleAddAmenityChange}
                      placeholder="e.g. Swimming Pool, Gym, WiFi"
                      style={
                        addAmenityErrors.amenityName
                          ? { borderColor: "#dc2626" }
                          : {}
                      }
                    />
                    {addAmenityErrors.amenityName && (
                      <span style={{ color: "#dc2626", fontSize: 11 }}>
                        {addAmenityErrors.amenityName}
                      </span>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Icon (emoji) *</label>
                    <input
                      type="text"
                      name="icon"
                      value={addAmenityForm.icon}
                      onChange={handleAddAmenityChange}
                      placeholder="e.g. 🏊 🏋 📶"
                      style={{
                        fontSize: 20,
                        ...(addAmenityErrors.icon
                          ? { borderColor: "#dc2626" }
                          : {}),
                      }}
                    />
                    {addAmenityErrors.icon && (
                      <span style={{ color: "#dc2626", fontSize: 11 }}>
                        {addAmenityErrors.icon}
                      </span>
                    )}
                    <div className="input-hint">
                      Paste any emoji as the icon
                    </div>
                  </div>
                </div>

                <div className="form-group mb12">
                  <label>Description</label>
                  <textarea
                    name="description"
                    value={addAmenityForm.description}
                    onChange={handleAddAmenityChange}
                    style={{ minHeight: 80 }}
                    placeholder="Detailed description shown to tenants when display mode includes description."
                  />
                </div>

                <div
                  style={{
                    background: "var(--bg-secondary)",
                    borderRadius: "var(--radius-md)",
                    padding: 14,
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      cursor: "pointer",
                    }}
                  >
                    <label className="toggle">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={addAmenityForm.isActive}
                        onChange={handleAddAmenityChange}
                      />
                      <span className="toggle-slider" />
                    </label>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>
                        Enable Amenity
                      </div>
                      <div
                        style={{ fontSize: 11, color: "var(--text-secondary)" }}
                      >
                        Only enabled amenities are shown to property owners when
                        adding a society or property
                      </div>
                    </div>
                  </label>

                  <div
                    style={{
                      borderTop: "0.5px solid var(--border-light)",
                      paddingTop: 12,
                    }}
                  >
                    <label
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        display: "block",
                        marginBottom: 8,
                      }}
                    >
                      Tenant Display Mode
                    </label>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                      }}
                    >
                      {[
                        {
                          value: "name",
                          label: "Name Only",
                          desc: 'show just the amenity name (e.g. "WiFi")',
                        },
                        {
                          value: "description",
                          label: "Description Only",
                          desc: "show only the description text",
                        },
                        {
                          value: "both",
                          label: "Name + Description",
                          desc: "show both (recommended)",
                        },
                      ].map((opt) => (
                        <label
                          key={opt.value}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            fontSize: 12,
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="radio"
                            name="showMode"
                            value={opt.value}
                            checked={addAmenityForm.showMode === opt.value}
                            onChange={handleAddAmenityChange}
                          />
                          <div>
                            <strong>{opt.label}</strong> — {opt.desc}
                          </div>
                        </label>
                      ))}
                    </div>
                    <div
                      style={{
                        marginTop: 10,
                        fontSize: 11,
                        color: "var(--text-tertiary)",
                      }}
                    >
                      This controls how the amenity appears in the tenant-facing
                      property listing and dashboard
                    </div>
                  </div>
                </div>

                {/* Live Preview */}
                <div
                  style={{
                    marginTop: 14,
                    padding: "12px 14px",
                    border: "0.5px solid var(--border-light)",
                    borderRadius: "var(--radius-md)",
                    background: "var(--bg-primary)",
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: "var(--text-tertiary)",
                      textTransform: "uppercase",
                      letterSpacing: ".06em",
                      marginBottom: 8,
                    }}
                  >
                    Tenant Preview
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 8,
                      fontSize: 13,
                    }}
                  >
                    <span style={{ fontSize: 20 }}>
                      {addAmenityForm.icon || "✨"}
                    </span>
                    <div>
                      {(addAmenityForm.showMode === "name" ||
                        addAmenityForm.showMode === "both") && (
                        <div style={{ fontWeight: 600 }}>
                          {addAmenityForm.amenityName || "Amenity Name"}
                        </div>
                      )}
                      {(addAmenityForm.showMode === "description" ||
                        addAmenityForm.showMode === "both") && (
                        <div
                          style={{
                            fontSize: 11,
                            color: "var(--text-secondary)",
                            marginTop: 2,
                            lineHeight: "1.5",
                          }}
                        >
                          {addAmenityForm.description ||
                            "Description will appear here"}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn"
                  onClick={closeAddAmenityModal}
                  disabled={addAmenityLoading}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleCreateAmenity}
                  disabled={addAmenityLoading}
                >
                  {addAmenityLoading ? "Saving..." : "Save Amenity"}
                </button>
              </div>
            </div>
          </div>
        )}

        {isModalOpenEditAmenity && (
          <div className="modal-overlay open">
            <div className="modal" style={{ width: 560 }}>
              <div className="modal-header">
                <div className="modal-title">Edit Amenity</div>
                <button className="modal-close" onClick={closeEditAmenityModal}>
                  ✕
                </button>
              </div>

              <div className="modal-body">
                <div className="form-grid mb12">
                  <div className="form-group">
                    <label>Amenity Name *</label>
                    <input
                      type="text"
                      name="amenityName"
                      value={editAmenityForm.amenityName}
                      onChange={handleEditAmenityChange}
                      placeholder="e.g. Swimming Pool, Gym, WiFi"
                      style={
                        editAmenityErrors.amenityName
                          ? { borderColor: "#dc2626" }
                          : {}
                      }
                    />
                    {editAmenityErrors.amenityName && (
                      <span style={{ color: "#dc2626", fontSize: 11 }}>
                        {editAmenityErrors.amenityName}
                      </span>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Icon (emoji) *</label>
                    <input
                      type="text"
                      name="icon"
                      value={editAmenityForm.icon}
                      onChange={handleEditAmenityChange}
                      placeholder="e.g. 🏊 🏋 📶"
                      style={{
                        fontSize: 20,
                        ...(editAmenityErrors.icon
                          ? { borderColor: "#dc2626" }
                          : {}),
                      }}
                    />
                    {editAmenityErrors.icon && (
                      <span style={{ color: "#dc2626", fontSize: 11 }}>
                        {editAmenityErrors.icon}
                      </span>
                    )}
                    <div className="input-hint">
                      Paste any emoji as the icon
                    </div>
                  </div>
                </div>

                <div className="form-group mb12">
                  <label>Description</label>
                  <textarea
                    name="description"
                    value={editAmenityForm.description}
                    onChange={handleEditAmenityChange}
                    style={{ minHeight: 80 }}
                    placeholder="Detailed description shown to tenants."
                  />
                </div>

                <div
                  style={{
                    background: "var(--bg-secondary)",
                    borderRadius: "var(--radius-md)",
                    padding: 14,
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      cursor: "pointer",
                    }}
                  >
                    <label className="toggle">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={editAmenityForm.isActive}
                        onChange={handleEditAmenityChange}
                      />
                      <span className="toggle-slider" />
                    </label>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>
                        Enable Amenity
                      </div>
                      <div
                        style={{ fontSize: 11, color: "var(--text-secondary)" }}
                      >
                        Only enabled amenities are shown to property owners when
                        adding a society or property
                      </div>
                    </div>
                  </label>

                  <div
                    style={{
                      borderTop: "0.5px solid var(--border-light)",
                      paddingTop: 12,
                    }}
                  >
                    <label
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        display: "block",
                        marginBottom: 8,
                      }}
                    >
                      Tenant Display Mode
                    </label>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                      }}
                    >
                      {[
                        {
                          value: "name",
                          label: "Name Only",
                          desc: 'show just the amenity name (e.g. "WiFi")',
                        },
                        {
                          value: "description",
                          label: "Description Only",
                          desc: "show only the description text",
                        },
                        {
                          value: "both",
                          label: "Name + Description",
                          desc: "show both (recommended)",
                        },
                      ].map((opt) => (
                        <label
                          key={opt.value}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            fontSize: 12,
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="radio"
                            name="showMode"
                            value={opt.value}
                            checked={editAmenityForm.showMode === opt.value}
                            onChange={handleEditAmenityChange}
                          />
                          <div>
                            <strong>{opt.label}</strong> — {opt.desc}
                          </div>
                        </label>
                      ))}
                    </div>
                    <div
                      style={{
                        marginTop: 10,
                        fontSize: 11,
                        color: "var(--text-tertiary)",
                      }}
                    >
                      This controls how the amenity appears in the tenant-facing
                      property listing and dashboard
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    marginTop: 14,
                    padding: "12px 14px",
                    border: "0.5px solid var(--border-light)",
                    borderRadius: "var(--radius-md)",
                    background: "var(--bg-primary)",
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: "var(--text-tertiary)",
                      textTransform: "uppercase",
                      letterSpacing: ".06em",
                      marginBottom: 8,
                    }}
                  >
                    Tenant Preview
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 8,
                      fontSize: 13,
                    }}
                  >
                    <span style={{ fontSize: 20 }}>
                      {editAmenityForm.icon || "✨"}
                    </span>
                    <div>
                      {(editAmenityForm.showMode === "name" ||
                        editAmenityForm.showMode === "both") && (
                        <div style={{ fontWeight: 600 }}>
                          {editAmenityForm.amenityName || "Amenity Name"}
                        </div>
                      )}
                      {(editAmenityForm.showMode === "description" ||
                        editAmenityForm.showMode === "both") && (
                        <div
                          style={{
                            fontSize: 11,
                            color: "var(--text-secondary)",
                            marginTop: 2,
                            lineHeight: "1.5",
                          }}
                        >
                          {editAmenityForm.description ||
                            "Description will appear here"}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn"
                  onClick={closeEditAmenityModal}
                  disabled={editAmenityLoading}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleUpdateAmenity}
                  disabled={editAmenityLoading}
                >
                  {editAmenityLoading ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        )}

        {isModalOpenDeleteAmenity && (
          <div className="modal-overlay open">
            <div className="modal" style={{ width: 400 }}>
              <div className="modal-header">
                <div className="modal-title">Confirm Delete</div>
                <button
                  className="modal-close"
                  onClick={() => setIsModalOpenDeleteAmenity(false)}
                >
                  ✕
                </button>
              </div>
              <div className="modal-body">
                <p style={{ fontSize: 14, color: "var(--text-primary)" }}>
                  Are you sure you want to delete this amenity? This action
                  cannot be undone.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  className="btn"
                  onClick={() => setIsModalOpenDeleteAmenity(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={confirmDeleteAmenity}
                >
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
